import { Controller } from "@hotwired/stimulus"
import { loadYandexMaps, boundaryBbox, boundaryCenter, createDistrictPolygon } from "../lib/yandex_maps"

export default class extends Controller {
  static values = {
    apikey: String,
    lat: Number,
    lng: Number,
    pointsUrl: String,
    cardsUrl: String,
    boundary: { type: Object, default: null },
    backUrl: String,
    preview: { type: Boolean, default: false }
  }

  static targets = [
    "container", "layer", "chip", "chipRingDot", "chipFillDot",
    "cardPanel", "cardPanelContent", "cardPanelTitle"
  ]

  connect() {
    if (!this.apikeyValue) return

    this.debounceTimer = null
    this.cardFetchAbort = null
    this.updateChips()
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    loadYandexMaps(this.apikeyValue).then(() => this.initMap())
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.cardFetchAbort) this.cardFetchAbort.abort()
    document.removeEventListener("keydown", this.boundKeydown)
  }

  goBack(event) {
    event.preventDefault()
    const referrer = document.referrer || ""
    const isSameOrigin = referrer.startsWith(window.location.origin)

    if (isSameOrigin) {
      window.history.back()
    } else if (this.backUrlValue) {
      if (window.Turbo?.visit) {
        window.Turbo.visit(this.backUrlValue)
      } else {
        window.location.href = this.backUrlValue
      }
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.closeCardPanel()
  }

  initMap() {
    const center = this.mapCenter()
    const zoom = this.mapZoom()

    this.map = new ymaps.Map(this.containerTarget, {
      center: center,
      zoom: zoom,
      controls: this.previewValue ? [] : ["zoomControl"]
    })

    this.containerTarget.map = this.map

    this.drawBoundary()
    this.fitToBoundary()

    if (this.previewValue) {
      this.disableInteraction()
    }

    this.clusterer = new ymaps.Clusterer({
      preset: "islands#invertedRedClusterIcons",
      clusterDisableClickZoom: true,
      clusterOpenBalloonOnClick: false
    })

    if (!this.previewValue) {
      this.clusterer.events.add("click", (event) => this.clusterClick(event))
    }

    this.map.geoObjects.add(this.clusterer)

    this.map.events.add("boundschange", () => this.scheduleFetch())

    if (!this.previewValue) {
      this.map.events.add("click", () => this.closeCardPanel())
    }

    this.scheduleFetch()
  }

  disableInteraction() {
    this.map.behaviors.disable([
      "drag",
      "scrollZoom",
      "dblClickZoom",
      "multiTouch",
      "leftMouseButtonMagnifier",
      "rightMouseButtonMagnifier"
    ])
  }

  mapCenter() {
    return boundaryCenter(this.boundaryValue) || [ this.latValue || 55.7558, this.lngValue || 37.6173 ]
  }

  mapZoom() {
    return this.boundaryValue ? 10 : 11
  }

  fitToBoundary() {
    const bbox = boundaryBbox(this.boundaryValue)
    if (!bbox) return

    this.map.setBounds(bbox, { checkZoomRange: true, zoomMargin: this.previewValue ? 10 : 20 })
  }

  drawBoundary() {
    const polygon = createDistrictPolygon(this.boundaryValue)
    if (!polygon) return

    this.map.geoObjects.add(polygon)
  }

  scheduleFetch() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchPoints(), 300)
  }

  layerChanged() {
    this.closeCardPanel()
    this.updateChips()
    this.scheduleFetch()
  }

  fetchPoints() {
    const layers = this.activeLayers()
    if (layers.length === 0) {
      this.updateMarkers([])
      return
    }

    const bounds = this.map.getBounds()
    const bbox = [
      bounds[0][0], bounds[0][1],
      bounds[1][0], bounds[1][1]
    ].join(",")

    const url = new URL(this.pointsUrlValue, window.location.origin)
    url.searchParams.set("bbox", bbox)
    url.searchParams.set("layers", layers.join(","))

    fetch(url.toString(), { headers: { "Accept": "application/json" } })
      .then((response) => response.json())
      .then((points) => this.updateMarkers(points))
  }

  activeLayers() {
    return this.layerTargets
      .filter((target) => target.checked)
      .map((target) => target.dataset.layer)
      .filter(Boolean)
  }

  updateChips() {
    this.layerTargets.forEach((input) => {
      const layer = input.dataset.layer
      const chip = this.chipTargets.find((el) => el.dataset.layer === layer)
      const ringDot = this.chipRingDotTargets.find((el) => el.closest("[data-layer]").dataset.layer === layer)
      const fillDot = this.chipFillDotTargets.find((el) => el.closest("[data-layer]").dataset.layer === layer)
      if (!chip) return

      const checked = input.checked
      chip.style.backgroundColor = checked ? "#ffffff" : "rgba(255, 255, 255, 0.6)"
      chip.style.boxShadow = checked ? "0 1px 2px 0 rgb(0 0 0 / 0.05)" : "none"
      chip.style.color = checked ? "#111827" : "#6b7280"

      if (ringDot) ringDot.style.display = checked ? "none" : "inline-block"
      if (fillDot) fillDot.style.display = checked ? "inline-block" : "none"
    })
  }

  updateMarkers(points) {
    this.clusterer.removeAll()

    const placemarks = points.map((point) => {
      const preset = this.presetFor(point.type)
      const placemark = new ymaps.Placemark(
        [point.lat, point.lng],
        {
          hintContent: point.title,
          point: point
        },
        { preset: preset, cursor: this.previewValue ? "default" : "pointer" }
      )
      if (!this.previewValue) {
        placemark.events.add("click", (event) => this.markerClick(event))
      }
      return placemark
    })

    this.clusterer.add(placemarks)
  }

  clusterClick(event) {
    const cluster = event.get("target")
    const geoObjects = cluster.getGeoObjects()
    const points = geoObjects
      .map((geoObject) => geoObject.properties.get("point"))
      .filter(Boolean)

    if (points.length === 0) return

    this.openCardPanel(points, `Объектов: ${points.length}`)
  }

  markerClick(event) {
    const placemark = event.get("target")
    const point = placemark.properties.get("point")
    if (!point) return

    this.openCardPanel([point])
  }

  openCardPanel(points, title = null) {
    if (this.cardFetchAbort) this.cardFetchAbort.abort()
    this.cardFetchAbort = new AbortController()

    if (this.hasCardPanelTitleTarget) {
      this.cardPanelTitleTarget.textContent = title || "На карте"
    }

    const url = new URL(this.cardsUrlValue, window.location.origin)
    points.forEach((point) => {
      url.searchParams.append("points[]", `${point.type}:${point.id}`)
    })

    this.cardPanelContentTarget.innerHTML = "<p class=\"text-gray-500 text-center py-4\">Загрузка…</p>"
    this.cardPanelTarget.classList.remove("hidden")

    fetch(url.toString(), {
      headers: { "Accept": "text/html" },
      signal: this.cardFetchAbort.signal
    })
      .then((response) => response.text())
      .then((html) => {
        this.cardPanelContentTarget.innerHTML = html
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        this.cardPanelContentTarget.innerHTML = "<p class=\"text-red-500 text-center py-4\">Не удалось загрузить данные</p>"
      })
  }

  closeCardPanel() {
    if (this.cardFetchAbort) {
      this.cardFetchAbort.abort()
      this.cardFetchAbort = null
    }
    if (this.hasCardPanelTarget) this.cardPanelTarget.classList.add("hidden")
    if (this.hasCardPanelTitleTarget) this.cardPanelTitleTarget.textContent = "На карте"
  }

  presetFor(type) {
    switch (type) {
      case "place":
        return "islands#blueDotIcon"
      case "post":
        return "islands#greenDotIcon"
      case "event":
        return "islands#redDotIcon"
      default:
        return "islands#grayDotIcon"
    }
  }
}
